import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Box, CircularProgress, Alert, Button, Chip,
  FormControl, InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { authApi } from '../api';

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(100);
  const [actionFilter, setActionFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await authApi.getAuditLogs(limit);
      setLogs(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [limit]);

  const actionColors = {
    login: 'info',
    register: 'success',
    update_user: 'warning',
    delete_user: 'error',
    scale_service: 'primary',
    restart_service: 'warning',
    remove_service: 'error',
    create_volume: 'success',
    remove_volume: 'error',
    test_notification: 'info',
    service_state_change: 'warning',
  };

  const filteredLogs = logs.filter(log => {
    if (actionFilter && log.action !== actionFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        log.action?.toLowerCase().includes(term) ||
        log.details?.toLowerCase().includes(term) ||
        log.username?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const uniqueActions = [...new Set(logs.map(l => l.action))].sort();

  if (loading && logs.length === 0) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Audit Log</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchLogs}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Action</InputLabel>
          <Select value={actionFilter} label="Action" onChange={e => setActionFilter(e.target.value)}>
            <MenuItem value="">All Actions</MenuItem>
            {uniqueActions.map(a => (
              <MenuItem key={a} value={a}>{a}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Limit</InputLabel>
          <Select value={limit} label="Limit" onChange={e => setLimit(e.target.value)}>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={250}>250</MenuItem>
            <MenuItem value={500}>500</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(log.timestamp).toLocaleString()}
                </TableCell>
                <TableCell>{log.username || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={log.action}
                    size="small"
                    color={actionColors[log.action] || 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {log.details}
                </TableCell>
              </TableRow>
            ))}
            {filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                    No audit logs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {filteredLogs.length} of {logs.length} entries
        </Typography>
      </Box>
    </Container>
  );
}

export default AuditLog;
